import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const Division = ({ updateMetrics }) => {
  const [dividend, setDividend] = useState('');
  const [divisor, setDivisor] = useState('');
  const [dividendDigits, setDividendDigits] = useState([]);
  const [divisionSteps, setDivisionSteps] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [customNumbers, setCustomNumbers] = useState({ dividend: '', divisor: '' });
  const [useCustomNumbers, setUseCustomNumbers] = useState(false);
  const [gradeLevel, setGradeLevel] = useState('1');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solutionSteps, setSolutionSteps] = useState(null);
  
  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 200, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRef = useRef(null);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { minDividend: 1, maxDividend: 99, minDivisor: 1, maxDivisor: 9 },        // Upto 2 digits
    '2': { minDividend: 10, maxDividend: 999, minDivisor: 2, maxDivisor: 99 },     // Upto 3 digits
    '3': { minDividend: 100, maxDividend: 9999, minDivisor: 2, maxDivisor: 99 },   // Upto 4 digits
    '4': { minDividend: 1000, maxDividend: 99999, minDivisor: 10, maxDivisor: 999 }, // Upto 5 digits
    '5': { minDividend: 10000, maxDividend: 999999, minDivisor: 10, maxDivisor: 999 }, // Upto 6 digits
    '6': { minDividend: 100000, maxDividend: 9999999, minDivisor: 100, maxDivisor: 999 }, // Upto 7 digits
    '7': { minDividend: 1000000, maxDividend: 99999999, minDivisor: 100, maxDivisor: 999 }, // Upto 8 digits
    '8': { minDividend: 10000000, maxDividend: 999999999, minDivisor: 100, maxDivisor: 999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 8; // Each division question takes about 8 lines
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Calculate division steps in advance
  const calculateDivisionSteps = (dividendNum, divisorNum) => {
    const steps = [];
    let currentWorkingNumber = '';
    let stepNumber = 0;

    const dividendStr = dividendNum.toString();
    let position = 0;
    let currentRemainderPosition = 0; // Track where the remainder is positioned
    let currentRemainderLength = 0; // Track the length of current remainder
    let workingDigitPosition = 0; // Track the position of the digit we're working on
    let previousStepOnesPosition = 0; // Track the ones position of the previous step's (remainder + bring down)

    while (position < dividendStr.length) {
      // Bring down next digit(s)
      if (currentWorkingNumber === '') {
        currentWorkingNumber = dividendStr[position];
        position++;
        currentRemainderPosition = 0; // First step starts at position 0
        currentRemainderLength = 0;
        workingDigitPosition = 0; // First step works on position 0
        previousStepOnesPosition = 0; // First step has no previous step
      } else {
        currentWorkingNumber += dividendStr[position];
        position++;
        // For subsequent steps, the partial product should align with the ones place
        // of the previous step's (remainder + bring down)
        workingDigitPosition = previousStepOnesPosition;
      }

      const workingNum = parseInt(currentWorkingNumber);
      
      // Find how many times divisor goes into working number
      const quotientDigit = Math.floor(workingNum / divisorNum);
      const partialProduct = quotientDigit * divisorNum;
      const remainder = workingNum - partialProduct;

      // Calculate alignment for proper positioning
      let partialProductStartPosition;
      let remainderStartPosition;
      
      // Partial product should align with the ones place of the current working number
      // Since we fill right to left, we need to calculate the start position
      const partialProductLength = partialProduct.toString().length;
      // The ones place is the rightmost digit of the current working number
      const currentWorkingOnesPosition = position - 1; // Position of the rightmost digit of current working number
      partialProductStartPosition = currentWorkingOnesPosition - (partialProductLength - 1); // Start position for right-to-left filling
      
      // Calculate the ones position of this step's (remainder + bring down) for the next step
      const remainderLength = remainder.toString().length;
      const bringDownLength = position < dividendStr.length ? 1 : 0; // Length of bring down digit
      const currentStepTotalLength = remainderLength + bringDownLength;
      // The ones position is the rightmost position of the current step's (remainder + bring down)
      // This should be based on the position of the current working number in the original dividend
      previousStepOnesPosition = position - 1; // The position of the last digit brought down
      
      // Remainder starts at the rightmost position of the partial product
      remainderStartPosition = partialProductStartPosition + partialProduct.toString().length - remainder.toString().length;

      // For the final step, align remainder with the ones place of the partial product
      if (position >= dividendStr.length) {
        // This is the final step - align remainder with the ones place
        const partialProductStr = partialProduct.toString();
        remainderStartPosition = partialProductStartPosition + partialProductStr.length - remainder.toString().length;
      }

      steps.push({
        stepNumber: stepNumber + 1,
        workingNumber: workingNum,
        quotientDigit: quotientDigit,
        partialProduct: partialProduct,
        remainder: remainder,
        bringDown: position < dividendStr.length ? dividendStr[position] : null,
        position: position - 1,
        // Calculate alignment for proper positioning
        remainderStartPosition: remainderStartPosition,
        partialProductStartPosition: partialProductStartPosition,
        bringDownPosition: position, // Where bring-down should be positioned
        isFinalStep: position >= dividendStr.length, // Flag to identify final step
        workingNumberLength: currentWorkingNumber.length,
        workingNumberPosition: currentRemainderPosition,
        workingDigitPosition: workingDigitPosition // Track the actual digit position
      });

      // Update for next step - the new working number starts at the position of the remainder
      currentWorkingNumber = remainder.toString();
      currentRemainderPosition = remainderStartPosition; // Next step starts where this remainder is
      currentRemainderLength = remainder.toString().length;
      stepNumber++;
    }

    return {
      steps: steps,
      finalQuotient: parseInt(steps.map(s => s.quotientDigit).join('')),
      finalRemainder: parseInt(currentWorkingNumber)
    };
  };

  // Generate random numbers for the problem
  const generateProblem = (isInitialLoad = false) => {
    if (useCustomNumbers && customNumbers.dividend && customNumbers.divisor) {
      setDividend(customNumbers.dividend);
      setDivisor(customNumbers.divisor);
      setDividendDigits(customNumbers.dividend.split('').map(digit => ({ value: digit, userValue: '' })));
    } else {
      const range = gradeRanges[gradeLevel];
      const newDividend = Math.floor(Math.random() * (range.maxDividend - range.minDividend + 1)) + range.minDividend;
      const newDivisor = Math.floor(Math.random() * (range.maxDivisor - range.minDivisor + 1)) + range.minDivisor;
      
      // Ensure clean division (no remainder for now)
      const cleanDividend = newDividend - (newDividend % newDivisor);
      
      setDividend(cleanDividend.toString());
      setDivisor(newDivisor.toString());
      setDividendDigits(cleanDividend.toString().split('').map(digit => ({ value: digit, userValue: '' })));
    }
    
    setDivisionSteps([]);
    setShowSolution(false);
    setSolutionSteps(null);
    setIsCorrect(null);
    setMessage('');
    if (!isInitialLoad) {
      setQuestionNumber(prev => prev + 1);
    }
    setTotalQuestions(prev => prev + 1);
    
    // Report metrics for new problem
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Calculate division steps when dividend or divisor changes
  useEffect(() => {
    if (dividend && divisor) {
      const dividendNum = parseInt(dividend);
      const divisorNum = parseInt(divisor);
      if (!isNaN(dividendNum) && !isNaN(divisorNum)) {
        const divisionResult = calculateDivisionSteps(dividendNum, divisorNum);
        setDivisionSteps(divisionResult.steps);
        console.log('Calculated division steps:', divisionResult);
      }
    }
  }, [dividend, divisor]);

  // Initialize user input states when division steps change
  useEffect(() => {
    if (divisionSteps.length > 0) {
      const newSteps = divisionSteps.map(step => ({
        ...step,
        userQuotientDigit: step.userQuotientDigit || '',
        userPartialProduct: step.userPartialProduct || [],
        userRemainder: step.userRemainder || [],
        userBringDown: step.userBringDown || ''
      }));
      setDivisionSteps(newSteps);
    }
  }, [divisionSteps.length]);

  // Handle dividend digit input
  const handleDividendDigitChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newDigits = [...dividendDigits];
      newDigits[index].userValue = value;
      setDividendDigits(newDigits);
    }
  };

  // Handle divisor input
  const handleDivisorChange = (value) => {
    if (value.length <= 3 && /^\d*$/.test(value)) {
      setDivisor(value);
    }
  };

  // Handle quotient digit input for a specific step
  const handleQuotientDigitChange = (stepIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newSteps = [...divisionSteps];
      if (newSteps[stepIndex]) {
        newSteps[stepIndex].userQuotientDigit = value;
        setDivisionSteps(newSteps);
      }
    }
  };

  // Handle partial product digit input for a specific step
  const handlePartialProductDigitChange = (stepIndex, digitIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newSteps = [...divisionSteps];
      if (newSteps[stepIndex]) {
        if (!newSteps[stepIndex].userPartialProduct) {
          newSteps[stepIndex].userPartialProduct = [];
        }
        newSteps[stepIndex].userPartialProduct[digitIndex] = value;
        setDivisionSteps(newSteps);
      }
    }
  };

  // Handle remainder digit input for a specific step
  const handleRemainderDigitChange = (stepIndex, digitIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newSteps = [...divisionSteps];
      if (newSteps[stepIndex]) {
        if (!newSteps[stepIndex].userRemainder) {
          newSteps[stepIndex].userRemainder = [];
        }
        newSteps[stepIndex].userRemainder[digitIndex] = value;
        setDivisionSteps(newSteps);
      }
    }
  };

  // Handle bring down digit input for a specific step
  const handleBringDownDigitChange = (stepIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newSteps = [...divisionSteps];
      if (newSteps[stepIndex]) {
        newSteps[stepIndex].userBringDown = value;
        setDivisionSteps(newSteps);
      }
    }
  };

  // Handle input focus for virtual keyboard
  const handleInputFocus = (type, stepIndex = null, digitIndex = null) => {
    let selector;
    if (type === 'dividend-digit') {
      selector = `[data-index="dividend-digit-${digitIndex}"]`;
    } else if (type === 'quotient-digit') {
      selector = `[data-index="quotient-digit-${stepIndex}"]`;
    } else if (type === 'partial-product-digit') {
      selector = `[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`;
    } else if (type === 'remainder-digit') {
      selector = `[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`;
    } else if (type === 'bring-down-digit') {
      selector = `[data-index="bring-down-digit-${stepIndex}"]`;
    } else {
      selector = `[data-index="${type}"]`;
    }
    
    const inputElement = document.querySelector(selector);
    if (inputElement && mathProblemRef.current) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRef.current.getBoundingClientRect();
      
      let x = rect.right - mathProblemRect.left + 5;
      let y = rect.top - mathProblemRect.top + 40;
      
      setKeyboardPosition({ x, y });
      setActiveInput({ type, stepIndex, digitIndex });
      setKeyboardVisible(true);
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Don't hide keyboard immediately to allow for virtual keyboard interaction
  };

  // Handle virtual keyboard key press
  const handleVirtualKeyPress = (key) => {
    if (!activeInput) return;
    
    const { type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    if (type === 'dividend-digit') {
      inputElement = document.querySelector(`[data-index="dividend-digit-${digitIndex}"]`);
      if (inputElement) {
        handleDividendDigitChange(digitIndex, key);
        inputElement.focus();
      }
    } else if (type === 'divisor') {
      inputElement = document.querySelector(`[data-index="divisor"]`);
      if (inputElement) {
        handleDivisorChange(key);
        inputElement.focus();
      }
    } else if (type === 'quotient-digit') {
      inputElement = document.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
        handleQuotientDigitChange(stepIndex, key);
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = document.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        handlePartialProductDigitChange(stepIndex, digitIndex, key);
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = document.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        handleRemainderDigitChange(stepIndex, digitIndex, key);
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = document.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
        handleBringDownDigitChange(stepIndex, key);
        inputElement.focus();
      }
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    if (!activeInput) return;
    
    const { type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    if (type === 'dividend-digit') {
      inputElement = document.querySelector(`[data-index="dividend-digit-${digitIndex}"]`);
      if (inputElement) {
        const currentValue = dividendDigits[digitIndex]?.userValue || '';
        handleDividendDigitChange(digitIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'divisor') {
      inputElement = document.querySelector(`[data-index="divisor"]`);
      if (inputElement) {
        handleDivisorChange(divisor.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'quotient-digit') {
      inputElement = document.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
        const currentValue = divisionSteps[stepIndex]?.userQuotientDigit || '';
        handleQuotientDigitChange(stepIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = document.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        const currentValue = divisionSteps[stepIndex]?.userPartialProduct?.[digitIndex] || '';
        handlePartialProductDigitChange(stepIndex, digitIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = document.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        const currentValue = divisionSteps[stepIndex]?.userRemainder?.[digitIndex] || '';
        handleRemainderDigitChange(stepIndex, digitIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = document.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
        const currentValue = divisionSteps[stepIndex]?.userBringDown || '';
        handleBringDownDigitChange(stepIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    if (!activeInput) return;
    
    const { type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    if (type === 'dividend-digit') {
      inputElement = document.querySelector(`[data-index="dividend-digit-${digitIndex}"]`);
      if (inputElement) {
        handleDividendDigitChange(digitIndex, '');
        inputElement.focus();
      }
    } else if (type === 'divisor') {
      inputElement = document.querySelector(`[data-index="divisor"]`);
      if (inputElement) {
        handleDivisorChange('');
        inputElement.focus();
      }
    } else if (type === 'quotient-digit') {
      inputElement = document.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
        handleQuotientDigitChange(stepIndex, '');
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = document.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        handlePartialProductDigitChange(stepIndex, digitIndex, '');
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = document.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
        handleRemainderDigitChange(stepIndex, digitIndex, '');
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = document.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
        handleBringDownDigitChange(stepIndex, '');
        inputElement.focus();
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, type, stepIndex = null, digitIndex = null) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      // Navigation logic will be implemented
    }
  };

  // Handle click outside to close keyboard
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (keyboardVisible && !event.target.closest('.virtual-keyboard') && 
          !event.target.closest('input') && 
          !event.target.closest('button')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [keyboardVisible]);

  // Validate answer
  const validateAnswer = () => {
    const expectedQuotient = Math.floor(parseInt(dividend) / parseInt(divisor));
    let userQuotient = '';
    
    // Build user quotient from all steps
    divisionSteps.forEach(step => {
      userQuotient += step.userQuotientDigit || '';
    });
    
    const userQuotientNum = parseInt(userQuotient) || 0;
    
    if (userQuotientNum === expectedQuotient) {
      setIsCorrect(true);
      setMessage('Correct! Well done!');
      setScore(prev => prev + 1);
      
      // Track metrics for correct answer
      metricsTracker.trackCheckAnswer('division', gradeLevel, true);
    } else {
      setIsCorrect(false);
      setMessage(`Incorrect. The correct answer is ${expectedQuotient}.`);
      
      // Track metrics for incorrect answer
      metricsTracker.trackCheckAnswer('division', gradeLevel, false);
    }
  };

  // Show solution steps
  const showSolutionSteps = () => {
    setShowSolution(true);
    setSolutionSteps(getSolutionSteps());
    
    // Track metrics for show solution
    metricsTracker.trackShowSolution('division', gradeLevel);
  };

  // Get solution steps
  const getSolutionSteps = () => {
    const dividendNum = parseInt(dividend);
    const divisorNum = parseInt(divisor);
    const quotientNum = Math.floor(dividendNum / divisorNum);
    const remainderNum = dividendNum % divisorNum;
    
    return {
      dividend: dividendNum,
      divisor: divisorNum,
      quotient: quotientNum,
      remainder: remainderNum,
      steps: divisionSteps
    };
  };

  // Generate problem on component mount
  useEffect(() => {
    generateProblem(true); // Pass true for initial load
  }, [gradeLevel]);



  // Calculate total height needed for vertical line to cover all steps
  const calculateVerticalLineHeight = () => {
    if (divisionSteps.length === 0) return 80;
    // Base height for dividend + each step (partial product + remainder + bring down)
    const baseHeight = 80; // dividend area
    const stepHeight = divisionSteps.length * 120; // reduced from 160px to 120px per step to prevent over-extension
    return Math.max(80, baseHeight + stepHeight);
  };

  // Calculate quotient margin to align with dividend's highest place value
  const calculateQuotientMargin = () => {
    // Quotient should be right-shifted 1 space (42px) as it starts above the divisor
    // For larger divisors, adjust the margin to maintain proper alignment
    const baseMargin = 42; // 1 space right shift
    const divisorLength = divisor.toString().length;
    
    // If divisor is larger than 1 digit, adjust margin to prevent left shifting
    if (divisorLength > 1) {
      // Adjust margin based on divisor length to maintain alignment
      return baseMargin + (divisorLength - 1) * 10; // Add 10px per additional digit
    }
    
    return baseMargin;
  };

  // Calculate horizontal line width to start from vertical line and extend properly
  const calculateHorizontalLineWidth = (startPosition, contentLength, stepIndex = null) => {
    // Start from the vertical line (which is at position 0) and extend to cover the content
    const startFromVerticalLine = 0;
    
    if (stepIndex !== null && stepIndex < divisionSteps.length) {
      // For subtraction lines, calculate width to extend to the bring-down value
      const step = divisionSteps[stepIndex];
      if (step.bringDown) {
        // Calculate the total width needed to reach the bring-down position
        // Add extra space for better user experience and differentiation
        const bringDownEndPosition = step.remainderStartPosition + step.remainder.toString().length + 1; // +1 for bring-down
        const width = Math.max(60, (bringDownEndPosition + 2) * 42); // +2 for extra space and better differentiation
        return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
      } else {
        // For final step without bring-down, extend to cover remainder
        const remainderEndPosition = step.remainderStartPosition + step.remainder.toString().length;
        const width = Math.max(60, (remainderEndPosition + 2) * 42); // +2 for extra space and better differentiation
        return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
      }
    } else {
      // For the horizontal line above dividend, use a more reasonable calculation
      const width = Math.max(60, Math.min((contentLength + 2) * 42, 800)); // +2 for extra space and better differentiation, cap at 800px
      return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
    }
  };

  return (
    <div className="division-container" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* 1. Operation Practice (Title + Description) */}
      <div className="page-title">
        <h2>Division Practice</h2>
        <p>Practice long division with step-by-step working.</p>
      </div>



      {/* Grade Level, Custom Numbers, and Generate Button */}
      <div className="options-section" style={{ marginBottom: '30px', textAlign: 'center' }}>
        <div className="grade-selector" style={{ marginBottom: '20px' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Level:</label>
          <select 
            value={gradeLevel} 
            onChange={(e) => {
              setGradeLevel(e.target.value);
              // Track level selection when user clicks dropdown
              metricsTracker.trackLevelSelected('division', e.target.value);
            }}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
              <option 
                key={level} 
                value={level.toString()}
                disabled={level > 3}
                style={{ color: level > 3 ? '#999' : 'inherit' }}
              >
                {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
              </option>
            ))}
          </select>
        </div>

        <div className="custom-numbers" style={{ marginBottom: '20px' }}>
          <label style={{ marginRight: '10px' }}>
            <input
              type="checkbox"
              checked={useCustomNumbers}
              onChange={(e) => setUseCustomNumbers(e.target.checked)}
              style={{ marginRight: '5px' }}
            />
            Use Custom Numbers
          </label>
          
          {useCustomNumbers && (
            <div className="custom-inputs" style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                <input
                  type="number"
                  placeholder="Dividend"
                  value={customNumbers.dividend}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 4) {
                      setCustomNumbers(prev => ({ ...prev, dividend: value }));
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    width: '120px'
                  }}
                />
                <input
                  type="number"
                  placeholder="Divisor"
                  value={customNumbers.divisor}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 4) {
                      setCustomNumbers(prev => ({ ...prev, divisor: value }));
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    width: '120px'
                  }}
                />
              </div>
              {(customNumbers.dividend.length > 4 || customNumbers.divisor.length > 4) && (
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: '#e74c3c', 
                  padding: '4px 8px',
                  backgroundColor: '#fdf2f2',
                  borderRadius: '4px',
                  border: '1px solid #fecaca'
                }}>
                  ⚠️ Upgrade to Pro Edition to use numbers with more than 4 digits
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={generateProblem}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
        >
          Generate New Problem
        </button>
      </div>

      {/* Question Number */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: '#2c3e50', margin: '0' }}>
          Question #{questionNumber}: {dividend}÷{divisor}=?
        </h3>
      </div>

      {/* Math Problem */}
      {dividend && divisor && divisionSteps.length > 0 && (
        <div 
          ref={mathProblemRef}
          className="math-problem"
          style={{
            position: 'relative',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            marginBottom: '30px',
            border: '2px solid #e9ecef'
          }}
        >
          {/* Traditional Long Division Layout */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '0px'
          }}>
            {/* Quotient Row - Above horizontal line */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              marginBottom: '5px',
              marginLeft: `${calculateQuotientMargin()}px` // Align with the highest place value of dividend, ensure non-negative
            }}>
              {divisionSteps.map((step, stepIndex) => (
                <input
                  key={stepIndex}
                  type="text"
                  value={step.userQuotientDigit || ''}
                  onChange={(e) => handleQuotientDigitChange(stepIndex, e.target.value)}
                  onFocus={() => handleInputFocus('quotient-digit', stepIndex)}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => handleKeyDown(e, 'quotient-digit', stepIndex)}
                  data-index={`quotient-digit-${stepIndex}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    textAlign: 'center',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#e74c3c',
                    marginRight: '2px',
                    outline: 'none',
                    backgroundColor: '#ffeaea'
                  }}
                  maxLength={1}
                  placeholder=""
                />
              ))}
            </div>

            {/* Main Division Structure */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              {/* Divisor */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '10px'
              }}>
                <input
                  type="text"
                  value={divisor}
                  readOnly
                  style={{
                    width: `${Math.max(40, divisor.toString().length * 20)}px`, // Auto-adjust width based on divisor length
                    height: '40px',
                    textAlign: 'center',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#333',
                    marginBottom: '5px',
                    backgroundColor: '#f8f9fa',
                    outline: 'none'
                  }}
                  maxLength={3}
                  placeholder="?"
                />
              </div>

              {/* Vertical Line */}
              <div style={{
                width: '2px',
                height: `${calculateVerticalLineHeight()}px`,
                backgroundColor: '#333',
                marginTop: '10px'
              }}></div>

              {/* Dividend and Working Area */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}>
                {/* Horizontal Line Above Dividend */}
                <div style={{
                  ...calculateHorizontalLineWidth(0, dividendDigits.length),
                  height: '2px',
                  backgroundColor: '#333',
                  marginBottom: '10px' // +1 space (10px) between horizontal line and dividend
                }}></div>

                {/* Dividend - Individual digit fields */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  gap: '2px'
                }}>
                  {dividendDigits.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      value={digit.value}
                      readOnly
                      style={{
                        width: '40px',
                        height: '40px',
                        textAlign: 'center',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333',
                        backgroundColor: '#f8f9fa',
                        outline: 'none'
                      }}
                      maxLength={1}
                      placeholder={digit.value}
                    />
                  ))}
                </div>

                {/* Division Steps */}
                {divisionSteps.map((step, stepIndex) => (
                  <div key={stepIndex} style={{ marginBottom: '10px' }}>
                    {/* Partial Product */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '5px',
                      marginLeft: `${step.partialProductStartPosition * 42}px` // Use calculated position
                    }}>
                      {step.partialProduct.toString().split('').map((digit, digitIndex) => (
                        <input
                          key={digitIndex}
                          type="text"
                          value={step.userPartialProduct?.[digitIndex] || ''}
                          onChange={(e) => handlePartialProductDigitChange(stepIndex, digitIndex, e.target.value)}
                          onFocus={() => handleInputFocus('partial-product-digit', stepIndex, digitIndex)}
                          onBlur={handleInputBlur}
                          onKeyDown={(e) => handleKeyDown(e, 'partial-product-digit', stepIndex, digitIndex)}
                          data-index={`partial-product-digit-${stepIndex}-${digitIndex}`}
                          style={{
                            width: '40px',
                            height: '40px',
                            textAlign: 'center',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#e8f5e8',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#27ae60',
                            marginRight: '2px',
                            outline: 'none'
                          }}
                          maxLength={1}
                          placeholder=""
                        />
                      ))}
                    </div>

                    {/* Subtraction Line */}
                    <div style={{ 
                      borderBottom: '2px solid #333',
                      ...calculateHorizontalLineWidth(0, step.partialProduct.toString().length, stepIndex),
                      height: '2px',
                      marginBottom: '10px' // +1 space (10px) between horizontal line and partial product
                    }}></div>

                    {/* Remainder and Bring Down on same row */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '5px'
                    }}>
                      {/* Remainder */}
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: `${step.remainderStartPosition * 42}px`
                      }}>
                        {step.remainder.toString().split('').map((digit, digitIndex) => (
                          <input
                            key={digitIndex}
                            type="text"
                            value={step.userRemainder?.[digitIndex] || ''}
                            onChange={(e) => handleRemainderDigitChange(stepIndex, digitIndex, e.target.value)}
                            onFocus={() => handleInputFocus('remainder-digit', stepIndex, digitIndex)}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleKeyDown(e, 'remainder-digit', stepIndex, digitIndex)}
                            data-index={`remainder-digit-${stepIndex}-${digitIndex}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              textAlign: 'center',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: '#fff3e0',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#f39c12',
                              marginRight: '2px',
                              outline: 'none'
                            }}
                            maxLength={1}
                            placeholder=""
                          />
                        ))}
                      </div>

                      {/* Bring Down on same row with arrow on right side */}
                      {step.bringDown && (
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: '2px' // Minimal gap between remainder and bring-down
                        }}>
                          <input
                            type="text"
                            value={step.userBringDown || ''}
                            onChange={(e) => handleBringDownDigitChange(stepIndex, e.target.value)}
                            onFocus={() => handleInputFocus('bring-down-digit', stepIndex)}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleKeyDown(e, 'bring-down-digit', stepIndex)}
                            data-index={`bring-down-digit-${stepIndex}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              textAlign: 'center',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#333',
                              backgroundColor: '#e8f4fd',
                              marginRight: '5px',
                              outline: 'none'
                            }}
                            maxLength={1}
                            placeholder=""
                          />
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: '#333'
                          }}>
                            ↓
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Virtual Keyboard */}
          {keyboardVisible && (
            <VirtualKeyboard
              onKeyPress={handleVirtualKeyPress}
              onBackspace={handleVirtualBackspace}
              onClear={handleVirtualClear}
              position={keyboardPosition}
              isVisible={keyboardVisible}
            />
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <button
          onClick={validateAnswer}
          style={{
            padding: '12px 24px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            fontWeight: 'bold',
            marginRight: '15px',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#229954'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
        >
          Check Answer
        </button>
        
        <button
          onClick={showSolutionSteps}
          style={{
            padding: '12px 24px',
            backgroundColor: '#9b59b6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#8e44ad'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#9b59b6'}
        >
          Show Solution
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          textAlign: 'center',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: isCorrect ? '#d4edda' : '#f8d7da',
          color: isCorrect ? '#155724' : '#721c24',
          border: `1px solid ${isCorrect ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      {/* Solution Steps */}
      {showSolution && solutionSteps && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>Step-by-Step Solution</h3>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <p><strong>Problem:</strong> {solutionSteps.dividend} ÷ {solutionSteps.divisor}</p>
            <p><strong>Answer:</strong> {solutionSteps.quotient} R {solutionSteps.remainder}</p>
            
            <div style={{ marginTop: '15px' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>Working Steps:</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}>Step</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}>Quotient Digit</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}>Partial Product</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}>Remainder</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        color: '#2c3e50',
                        fontWeight: 'bold'
                      }}>Bring Down</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solutionSteps.steps.map((step, index) => (
                      <tr key={index} style={{
                        borderBottom: '1px solid #dee2e6'
                      }}>
                        <td style={{
                          padding: '12px',
                          fontWeight: 'bold',
                          color: '#2c3e50'
                        }}>{index + 1}</td>
                        <td style={{
                          padding: '12px',
                          color: '#e74c3c',
                          fontWeight: 'bold'
                        }}>{step.quotientDigit}</td>
                        <td style={{
                          padding: '12px',
                          color: '#27ae60',
                          fontWeight: 'bold'
                        }}>
                          {step.partialProduct} ({step.quotientDigit} × {solutionSteps.divisor})
                        </td>
                        <td style={{
                          padding: '12px',
                          color: '#f39c12',
                          fontWeight: 'bold'
                        }}>
                          {step.remainder} ({step.workingNumber} - {step.partialProduct})
                        </td>
                        <td style={{
                          padding: '12px',
                          color: '#333'
                        }}>
                          {step.bringDown ? `${step.bringDown} → (${step.remainder} + ${step.bringDown})` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Division; 